import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

/**
 * A gradient sky dome with a golden-hour sun. It's an inverted sphere pinned to
 * the camera (infiniteDistance), drawn first with depth-write off so everything
 * renders in front of it. The custom shader means scene fog never touches it, so
 * the horizon colour is exactly the fog colour and the terrain melts into the sky.
 */
Effect.ShadersStore["skyVertexShader"] = /* glsl */ `
precision highp float;
attribute vec3 position;
uniform mat4 viewProjection;
uniform mat4 world;
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = viewProjection * world * vec4(position, 1.0);
}
`;

Effect.ShadersStore["skyFragmentShader"] = /* glsl */ `
precision highp float;
varying vec3 vDir;
uniform vec3 horizon;
uniform vec3 zenith;
uniform vec3 sunDir;   // direction TO the sun
uniform vec3 sunColor;
void main() {
  vec3 d = normalize(vDir);
  float t = clamp(d.y * 1.15, 0.0, 1.0);
  vec3 col = mix(horizon, zenith, pow(t, 0.62));
  float s = max(0.0, dot(d, normalize(sunDir)));
  float disc = smoothstep(0.9965, 0.9992, s);       // the sun's disc
  float glow = pow(s, 48.0) * 0.6 + pow(s, 7.0) * 0.14; // warm halo
  col += sunColor * (disc * 1.6 + glow);
  col += horizon * pow(1.0 - abs(d.y), 7.0) * 0.18;  // thicker haze at the horizon
  gl_FragColor = vec4(col, 1.0);
}
`;

export function createSky(scene: Scene, sunToward: Vector3): Mesh {
  const dome = CreateSphere("sky", { diameter: 1000, segments: 16, sideOrientation: Mesh.BACKSIDE }, scene);
  dome.infiniteDistance = true; // follows the camera → unreachable
  dome.isPickable = false;
  dome.applyFog = false;

  const mat = new ShaderMaterial("sky", scene, "sky", {
    attributes: ["position"],
    uniforms: ["world", "viewProjection", "horizon", "zenith", "sunDir", "sunColor"],
  });
  mat.setColor3("horizon", new Color3(0.86, 0.64, 0.42)); // ≈ fog colour
  mat.setColor3("zenith", new Color3(0.28, 0.36, 0.56)); // cool dusk overhead
  mat.setVector3("sunDir", sunToward.normalizeToNew());
  mat.setColor3("sunColor", new Color3(1.0, 0.82, 0.55));
  mat.backFaceCulling = false;
  mat.disableDepthWrite = true;
  dome.material = mat;
  dome.renderingGroupId = 0;
  return dome;
}
